import { Button } from '@/components/ui/button.tsx'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { useAuth } from '@/hooks/use-auth.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export const schema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/auth/sign-in')({
  component: RouteComponent,
  validateSearch: zodValidator(schema),
  head: () => ({
    meta: [
      {
        title: 'Sign in | Okami',
      },
    ],
  }),
})

const formLoginSchema = z.object({
  email: z.string().email('Informe um email válido'),
  password: z
    .string({
      required_error: 'Informe uma senha válida',
      invalid_type_error: "'Informe uma senha válida'",
    })
    .min(8, 'Informe uma senha válida'),
})

type FormLogin = z.infer<typeof formLoginSchema>

function RouteComponent() {
  const { login } = useAuth()
  const { redirect } = Route.useSearch()
  const navigate = Route.useNavigate()

  const [canShowPassword, setCanShowPassword] = useState(false)

  const form = useForm<FormLogin>({
    resolver: zodResolver(formLoginSchema),
    values: {
      email: '',
      password: '',
    },
  })

  async function handleSign(payload: FormLogin) {
    await login(payload.email, payload.password)

    navigate({
      to: redirect ?? '/',
      replace: true,
    })
  }

  function handleTogglePasswordShow() {
    setCanShowPassword((prev) => !prev)
  }

  const isPending = form.formState.isSubmitting

  return (
    <div className="p-8">
      <div className="flex w-[320px] flex-col justify-center gap-6">
        <header className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Acessar Painel</h1>
          <p className="text-sm text-muted-foreground">Acompanhe suas obras favoritas</p>
        </header>
        <Form {...form}>
          <form className="flex flex-col gap-4 " onSubmit={form.handleSubmit(handleSign)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="okami@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <Input type={canShowPassword ? 'text' : 'password'} placeholder="*********" {...field} />
                  </FormControl>
                  {canShowPassword ? (
                    <EyeOff onClick={handleTogglePasswordShow} className="absolute right-2 top-8" />
                  ) : (
                    <Eye onClick={handleTogglePasswordShow} className="absolute right-2 top-8" />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button disabled={isPending || !form.formState.isValid} className="w-full" type="submit">
              {isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : 'Login'}
            </Button>
            <div className="flex flex-col gap-2 text-center">
              <Button disabled={isPending} asChild type="button" variant="link">
                <Link to="/auth/sign-up">Não possui uma conta ? </Link>
              </Button>

              <Button disabled={isPending} asChild type="button" variant="link">
                <Link to="/auth/password/send-reset-email">Esqueceu a senha ?</Link>
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
